// frontend/src/components/widgets/ClockWidget.js - RESPONSIVE VERSION
import React, { useState, useEffect } from 'react';
import { Clock as ClockIcon, Calendar, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ClockWidget = ({ config = {}, screenSize = 'FHD' }) => {
  const { t, i18n } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Default config values
  const {
    format = '24h',
    showDate = true,
    showSeconds = true,
    timezone = 'auto',
    dateFormat = 'long',
    theme = 'modern',
    layout = 'dashboard',
    showTimezone = false
  } = config;

  // ✅ RESPONSIVE: Get multipliers based on screen size
  const getResponsiveMultipliers = () => {
    const multipliers = {
      SM: { font: 0.35, spacing: 0.4, icon: 0.4 },   // 640px - Mobile
      MD: { font: 0.5, spacing: 0.55, icon: 0.5 },   // 768px - Tablet
      HD: { font: 0.7, spacing: 0.75, icon: 0.7 },   // 1024px - HD
      FHD: { font: 1.0, spacing: 1.0, icon: 1.0 },   // 1920px - Full HD
      QHD: { font: 1.2, spacing: 1.15, icon: 1.2 },  // 2560px - 2K
      '4K': { font: 1.5, spacing: 1.4, icon: 1.5 }   // 3840px+ - 4K
    };
    return multipliers[screenSize] || multipliers.FHD;
  };

  const m = getResponsiveMultipliers();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, showSeconds ? 1000 : 60000);

    return () => clearInterval(interval);
  }, [showSeconds]);

  // Map i18n language to browser locale
  const getLocale = () => {
    const langMap = {
      'de': 'de-CH',
      'en': 'en-US',
      'fr': 'fr-CH',
      'it': 'it-CH'
    };
    return langMap[i18n.language] || 'de-CH';
  };

  // Format time based on config
  const formatTime = () => {
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      ...(showSeconds && { second: '2-digit' }),
      hour12: format === '12h',
      ...(timezone !== 'auto' && { timeZone: timezone })
    };

    return currentTime.toLocaleTimeString(getLocale(), options);
  };

  // Get individual time components
  const getTimeComponents = () => {
    const timeString = formatTime();
    const parts = timeString.split(':');
    
    return {
      hours: parts[0],
      minutes: parts[1],
      seconds: showSeconds ? parts[2]?.replace(/\s*(AM|PM)/, '') : null,
      period: format === '12h' ? (currentTime.getHours() >= 12 ? 'PM' : 'AM') : null
    };
  };

  // Format date based on config
  const formatDate = () => {
    if (!showDate) return null;

    const options = {};
    
    switch (dateFormat) {
      case 'long':
        options.weekday = 'long';
        options.year = 'numeric';
        options.month = 'long';
        options.day = 'numeric';
        break;
      case 'short':
        options.year = 'numeric';
        options.month = 'short';
        options.day = 'numeric';
        break;
      case 'numeric':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case 'iso':
        return currentTime.toISOString().split('T')[0];
      default:
        options.weekday = 'long';
        options.year = 'numeric';
        options.month = 'long';
        options.day = 'numeric';
    }

    if (timezone !== 'auto') {
      options.timeZone = timezone;
    }

    return currentTime.toLocaleDateString(getLocale(), options);
  };

  // Get day of week
  const getDayOfWeek = () => {
    const options = { weekday: 'long' };
    if (timezone !== 'auto') {
      options.timeZone = timezone;
    }
    return currentTime.toLocaleDateString(getLocale(), options);
  };

  // Get full date
  const getFullDate = () => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    if (timezone !== 'auto') {
      options.timeZone = timezone;
    }
    return currentTime.toLocaleDateString(getLocale(), options);
  };

  // Get theme styles
  const getThemeStyles = () => {
    const themes = {
      modern: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        textColor: 'text-white',
        shadowColor: 'rgba(0, 0, 0, 0.3)'
      },
      classic: {
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        textColor: 'text-white',
        shadowColor: 'rgba(0, 0, 0, 0.5)'
      },
      minimal: {
        background: '#ffffff',
        textColor: 'text-gray-900',
        shadowColor: 'rgba(0, 0, 0, 0.1)'
      },
      dark: {
        background: '#000000',
        textColor: 'text-white',
        shadowColor: 'rgba(255, 255, 255, 0.1)'
      },
      weather: {
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        textColor: 'text-white',
        shadowColor: 'rgba(0, 0, 0, 0.3)'
      }
    };

    return themes[theme] || themes.modern;
  };

  const themeStyles = getThemeStyles();
  const timeComponents = getTimeComponents();

  // Classic layout - ✅ RESPONSIVE
  if (layout === 'classic') {
    const time = formatTime();
    const date = formatDate();

    return (
      <div 
        className={`w-full h-full flex flex-col items-center justify-center ${themeStyles.textColor}`}
        style={{ 
          background: themeStyles.background,
          fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        }}
      >
        <div className="text-center px-8 py-12">
          {/* Clock Icon - RESPONSIVE */}
          <div className="mb-8 flex justify-center">
            <ClockIcon 
              size={80 * m.icon} 
              strokeWidth={1.5} 
              className="opacity-90"
              style={{ 
                filter: `drop-shadow(0 4px 8px ${themeStyles.shadowColor})` 
              }}
            />
          </div>

          {/* Time Display - RESPONSIVE */}
          <div 
            className="font-bold mb-4"
            style={{ 
              fontSize: `${10 * m.font}rem`,
              lineHeight: 1,
              textShadow: `0 4px 12px ${themeStyles.shadowColor}`,
              letterSpacing: '0.05em'
            }}
          >
            {time}
          </div>

          {/* Date Display - RESPONSIVE */}
          {showDate && date && (
            <div 
              className="flex items-center justify-center gap-3 opacity-90"
              style={{ 
                fontSize: `${3 * m.font}rem`,
                textShadow: `0 2px 6px ${themeStyles.shadowColor}`
              }}
            >
              <Calendar size={36 * m.icon} strokeWidth={1.5} />
              <span>{date}</span>
            </div>
          )}

          {/* Timezone Info - RESPONSIVE */}
          {showTimezone && timezone !== 'auto' && (
            <div 
              className="mt-6 text-sm opacity-75"
              style={{ 
                fontSize: `${1.25 * m.font}rem`
              }}
            >
              {timezone.replace('_', ' ')}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Dashboard layout - ✅ RESPONSIVE WITH GLASSMORPHISM
  return (
    <div 
      className={`w-full h-full flex items-center justify-center ${themeStyles.textColor} relative overflow-hidden`}
      style={{ 
        background: themeStyles.background,
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        minHeight: '100vh',
        padding: `${8 * m.spacing}px`
      }}
    >
      {/* Animated Background Particles */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
          `,
          animation: 'float 20s ease-in-out infinite'
        }}
      />

      {/* Main Container with Glassmorphism - RESPONSIVE */}
      <div 
        className="relative z-10 w-full rounded-[40px]"
        style={{
          maxWidth: '95%',
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          animation: 'fadeIn 0.8s ease-out',
          padding: `${35 * m.spacing}px ${80 * m.spacing}px ${60 * m.spacing}px ${80 * m.spacing}px`
        }}
      >
        
        {/* Timezone Header (optional) - RESPONSIVE */}
        {showTimezone && timezone !== 'auto' && (
          <div 
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: `${24 * m.spacing}px`,
              marginBottom: `${45 * m.spacing}px`,
              fontSize: `${58 * m.font}px`,
              fontWeight: '300',
              letterSpacing: '2.5px',
              color: 'rgba(255, 255, 255, 0.95)'
            }}
          >
            <MapPin size={64 * m.icon} style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' }} />
            <span>{timezone.replace('_', ' ')}</span>
          </div>
        )}

        {/* Main Content - RESPONSIVE */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${60 * m.spacing}px` }}>
          
          {/* Time Display - Centered, HUGE - RESPONSIVE */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            
            {/* Clock Icon - RESPONSIVE */}
            <div 
              style={{ 
                fontSize: `${180 * m.icon}px`,
                marginBottom: `${40 * m.spacing}px`,
                filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))',
                animation: 'pulse 4s ease-in-out infinite'
              }}
            >
              <ClockIcon size={180 * m.icon} strokeWidth={1.5} className="opacity-90" />
            </div>

            {/* Time - GIGANTIC - RESPONSIVE */}
            <div 
              style={{ 
                fontSize: `${240 * m.font}px`,
                fontWeight: '200',
                color: '#ffffff',
                lineHeight: '1',
                marginBottom: `${20 * m.spacing}px`,
                textShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                letterSpacing: `${-8 * m.font}px`,
                display: 'flex',
                alignItems: 'baseline',
                gap: `${20 * m.spacing}px`
              }}
            >
              <span>{timeComponents.hours}</span>
              <span style={{ opacity: 0.8 }}>:</span>
              <span>{timeComponents.minutes}</span>
              {showSeconds && (
                <>
                  <span style={{ opacity: 0.8 }}>:</span>
                  <span style={{ fontSize: `${180 * m.font}px` }}>{timeComponents.seconds}</span>
                </>
              )}
              {timeComponents.period && (
                <span style={{ fontSize: `${100 * m.font}px`, marginLeft: `${30 * m.spacing}px`, opacity: 0.9 }}>
                  {timeComponents.period}
                </span>
              )}
            </div>

            {/* Day of Week - RESPONSIVE */}
            {showDate && (
              <div 
                style={{ 
                  fontSize: `${56 * m.font}px`,
                  color: 'rgba(255, 255, 255, 0.95)',
                  marginBottom: `${24 * m.spacing}px`,
                  fontWeight: '300',
                  letterSpacing: '1.5px'
                }}
              >
                {getDayOfWeek()}
              </div>
            )}

            {/* Full Date - RESPONSIVE */}
            {showDate && (
              <div 
                style={{ 
                  fontSize: `${36 * m.font}px`,
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: '300',
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${16 * m.spacing}px`
                }}
              >
                <Calendar size={42 * m.icon} strokeWidth={1.5} style={{ opacity: 0.9 }} />
                <span>{getFullDate()}</span>
              </div>
            )}

          </div>

        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default ClockWidget;